import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LoggerService } from '@core/logging/logger.service';

/** Developer-facing view of the structured log (§9.1), shown with `?debug=1`. */
@Component({
  selector: 'app-logs-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  template: `
    <div class="logs-panel">
      <div class="logs-header">
        <h3>Logs</h3>
        <button (click)="clearLogs()" class="clear-btn">Clear</button>
      </div>
      <div class="logs-container">
        @for (log of logger.events(); track $index) {
          <div [class]="'log-entry log-' + log.level">
            <span class="timestamp">{{ log.ts | date: 'HH:mm:ss.SSS' }}</span>
            <span class="level">{{ log.level | uppercase }}</span>
            <span class="subsystem">{{ log.subsystem }}</span>
            <span class="message">{{ log.event }}</span>
            @if (log.data) {
              <span class="details">{{ log.data | json }}</span>
            }
          </div>
        }
      </div>
    </div>
  `,
  styles: [`
    .logs-panel {
      display: flex;
      flex-direction: column;
      height: 300px;
      border: 1px solid #ccc;
      background: #1e1e1e;
      color: #d4d4d4;
      font-family: 'Courier New', monospace;
      font-size: 12px;
    }

    .logs-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.5rem;
      border-bottom: 1px solid #ccc;
      background: #252526;
    }

    h3 {
      margin: 0;
      font-size: 1rem;
    }

    .clear-btn {
      padding: 0.25rem 0.75rem;
      background: #444;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 11px;

      &:hover {
        background: #555;
      }
    }

    .logs-container {
      flex: 1;
      overflow-y: auto;
      padding: 0.5rem;
    }

    .log-entry {
      margin-bottom: 0.25rem;
      padding: 0.25rem 0.5rem;
      display: flex;
      gap: 0.5rem;
      align-items: flex-start;
      border-radius: 2px;

      &.log-info {
        background: rgba(79, 195, 247, 0.1);
        color: #4fc3f7;
      }

      &.log-error {
        background: rgba(244, 67, 54, 0.1);
        color: #f44336;
      }

      &.log-warn {
        background: rgba(255, 193, 7, 0.1);
        color: #ffc107;
      }

      &.log-debug {
        background: rgba(156, 39, 176, 0.1);
        color: #9c27b0;
      }
    }

    .timestamp {
      color: #888;
      min-width: 100px;
    }

    .level {
      font-weight: bold;
      min-width: 50px;
    }

    .subsystem {
      color: #6b8f3d;
      min-width: 70px;
    }

    .message {
      flex: 1;
    }

    .details {
      color: #666;
      word-break: break-all;
      max-width: 300px;
    }
  `],
})
export class LogsPanelComponent {
  readonly logger = inject(LoggerService);

  clearLogs(): void {
    this.logger.clear();
  }
}
